import { useEffect, useState } from 'react';

import config from 'config';
import { useRouter } from 'next/router';

import getBuilderStateContentVariantData from '~/builder/utils/getBuilderStateContentVariantData';
import { urlFiltersToCioFacets } from '~/grid/utils/cioUtils';
import useProfileSizes from '~/pdp/utils/useProfileSizes';
import useGender from '~/shared/utils/useGender';
import { useSession } from '~/techstyle-shared/redux-core';

import {
  formatGridGenderForAutofilters,
  getAutoFilterParams,
} from '../utils/autofilters';
import { isProductsV2SizeFacet } from '../utils/filters';
import { getNewQueryParamString, QueryParamBehavior } from '../utils/routing';

import useGridInputs, { GRID_TYPES } from './useGridInputs';

/**
 * This hook is used to apply automatic filters to the product grid based on the user's
 * size preferences and the grid gender. It only applies the filters if the user
 * has not already selected them in the URL and if the filters are valid (i.e., they exist
 * in the facets for the current grid). E.g. do not include "size_top" for a bottoms only grid
 */
export const useAutoFilters = ({
  builderState,
  gridGender,
  facets = [],
  isPrimaryProductsLoading,
}) => {
  const router = useRouter();
  const [autofilterProcessed, setAutofilterProcessed] = useState(false);
  const { profileSizes } = useProfileSizes({ isPdp: false });
  const { gender: userGender } = useGender();
  const { isLoggedIn } = useSession();
  const { gridType } = useGridInputs();

  const pageData = getBuilderStateContentVariantData({
    builderStateContent: builderState?.content,
  });

  const automaticSizeFiltering = pageData?.automaticSizeFiltering;

  const enableBuilderGenderAutofilter = pageData?.enableBuilderGenderAutofilter;
  const automaticGenderFiltering = enableBuilderGenderAutofilter && isLoggedIn;
  const remapYittySizes = config.get('public.brand.features.remapYittySizes');

  const autofilterParams = getAutoFilterParams({
    gridGender: formatGridGenderForAutofilters(gridGender),
    profileSizes,
    userGender,
    automaticSizeFilteringType: automaticSizeFiltering,
    automaticGenderFiltering,
  });

  // Only normalize this for Fabletics size filters for products/v2
  const normalizedAutofilterParams = Object.entries(autofilterParams).reduce(
    (acc, [key, value]) => {
      const shouldMapFableticsSizeFacet =
        remapYittySizes &&
        gridType === GRID_TYPES.PRODUCTS_V2_GRID &&
        isProductsV2SizeFacet(key);

      if (!shouldMapFableticsSizeFacet) {
        return { ...acc, [key]: value };
      }

      const sizesToNormalize = ['5X', '6X'];
      if (sizesToNormalize.includes(value)) {
        return { ...acc, [key]: '4X' };
      }

      return { ...acc, [key]: value };
    },
    {}
  );

  const validAutofilterParams = Object.entries(
    normalizedAutofilterParams
  ).reduce((acc, [key, value]) => {
    // Exclude filters that do not exist in facets for the current grid,
    // e.g. do not include "size_top" for a bottoms only grid
    const mappedKey =
      gridType === GRID_TYPES.PRODUCTS_V2_GRID
        ? key
        : urlFiltersToCioFacets[key] || key;
    const facet = facets?.find((facet) => facet?.name === mappedKey);
    if (!facet) {
      return acc;
    }

    // Exclude empty filters
    if (Array.isArray(value) && value.length > 0) {
      return { ...acc, [key]: value };
    }

    // Exclude empty strings
    if (typeof value === 'string' && value !== '') {
      return { ...acc, [key]: value };
    }

    return acc;
  }, {});

  useEffect(() => {
    // Since autofilters depend on facets and facet data comes from product fetch,
    // wait until initial product fetch (not subsequent fetches from pagination) is complete before applying autofilters
    if (isPrimaryProductsLoading) {
      return;
    }

    // useProfileSizes returns `false` while customer/sessionDetails load. If we
    // flip autofilterProcessed during that window, the post-quiz first render
    // (FLBUGS-2964) locks out the apply when profileSizes resolves seconds later.
    if (profileSizes === false) {
      return;
    }

    // If there are no valid autofilter params (either already selected in url or empty)
    // or no facets from product fetch, do nothing.
    if (Object.keys(validAutofilterParams).length === 0 || !facets?.length) {
      setAutofilterProcessed(true);
      return;
    }

    if (!autofilterProcessed) {
      const [asPathname, query] = router.asPath.split('?');
      const newQueryParamString = getNewQueryParamString({
        query,
        newQueryParams: validAutofilterParams,
        queryParamBehavior: QueryParamBehavior.APPEND,
      });

      if (!newQueryParamString) {
        setAutofilterProcessed(true);
        return;
      }

      const handleRouteChangeComplete = () => {
        setAutofilterProcessed(true);
        router.events.off('routeChangeComplete', handleRouteChangeComplete);
      };

      router.events.on('routeChangeComplete', handleRouteChangeComplete);

      router.replace(router.pathname, `${asPathname}?${newQueryParamString}`, {
        shallow: true,
      });
    }
  }, [
    autofilterProcessed,
    facets.length,
    validAutofilterParams,
    router,
    isPrimaryProductsLoading,
    profileSizes,
  ]);

  return {
    allAutofilterParams: autofilterParams,
    validAutofilterParams,
    autofilterProcessed,
  };
};
